/**
 * AI agent store — multi-turn propose/execute conversation with the
 * shim's `/api/ai/propose` + `/api/ai/execute-proposal` endpoints.
 *
 * The drawer's transcript lives here. localStorage is still the snapshot for
 * the *currently-open* turn (so a refresh doesn't lose mid-conversation
 * state), but every send/execute also mirrors to the server-side history
 * tables — see [opus/api-shim/auth.py] (agent_conversations / agent_messages).
 *
 * This means:
 *   - The /agent history page can list a user's full archive across browsers.
 *   - Switching browsers still loses the *unsent* draft (input box) but not
 *     past turns; you can reopen the same conversation from /agent.
 *
 * Persistence is best-effort: a failing mirror call never blocks the local
 * UI flow. If the network drops mid-conversation, the next send will create
 * a fresh server-side conversation row.
 */
import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { post as apiPost, get as apiGet, patch as apiPatch, del as apiDel, ApiError } from "@/api/client";

/** Per-tool-call breadcrumb the agent emits for the UI's read trace. */
export interface ReadTraceEntry {
  tool: string;
  args: Record<string, unknown>;
  resultPreview: string;
}

/** A proposal returned by /propose — round-tripped verbatim to /execute. */
export interface AgentProposal {
  tool: string;
  workflowSlug: string;
  args: Record<string, unknown>;
  reasoning: string;
  queuePayload: Record<string, unknown>;
  /** Server-derived: ISO timestamp of the most recent run of this tool's
   *  workflow_slug, or null if it's never been run. Drives the "last run X
   *  ago" tag on the proposal confirm card. */
  lastRunAt?: string | null;
}

/** One message in the visible transcript. The store keeps a flat list so
 *  the drawer renders top-to-bottom in order.
 *
 *  Each variant carries an optional `serverId` — the id returned by the
 *  shim's POST .../messages/ when this message was mirrored to durable
 *  storage. The store uses it to PATCH the same row on status changes
 *  (proposal pending → executed/cancelled). Missing serverId means the
 *  mirror call failed and we silently fall back to local-only persistence
 *  for that one message.
 */
export type AgentMessage =
  | {
      kind: "user";
      text: string;
      sentAt: string;
      serverId?: string;
    }
  | {
      kind: "answer";
      text: string;
      readTrace: ReadTraceEntry[];
      model: string;
      elapsedMs: number;
      receivedAt: string;
      serverId?: string;
    }
  | {
      kind: "proposal";
      proposal: AgentProposal;
      readTrace: ReadTraceEntry[];
      model: string;
      elapsedMs: number;
      receivedAt: string;
      /** "pending" → confirm card showing; "executed" → the user clicked Run
       *  and we got back a workflow_runs id; "cancelled" → user clicked X. */
      status: "pending" | "executing" | "executed" | "cancelled";
      /** Set once executed — link to the row in /workflows/runs/. */
      runId?: string;
      serverId?: string;
    }
  | {
      kind: "error";
      text: string;
      receivedAt: string;
      serverId?: string;
    };

/** Models the user can pick. Mirrors the backend's ALLOWED_MODELS set —
 *  if these drift the backend silently falls back to its default. */
export const AGENT_MODELS = [
  { id: "claude-sonnet-4-6",          label: "Sonnet 4.6", hint: "best tool-use · default" },
  { id: "claude-haiku-4-5-20251001",  label: "Haiku 4.5",  hint: "faster · cheaper" },
  { id: "claude-opus-4-7",            label: "Opus 4.7",   hint: "most capable · slowest" },
] as const;
export type AgentModelId = (typeof AGENT_MODELS)[number]["id"];

/** A row from GET /api/ai/conversations/. */
export interface ServerConversation {
  id: string;
  title: string;
  model: string | null;
  startedAt: string;
  lastAt: string;
  messageCount: number;
}

interface State {
  open: boolean;
  /** "idle" between turns; "thinking" while /propose is in flight. */
  status: "idle" | "thinking";
  messages: AgentMessage[];
  /** Server-side conversation id for the currently-open thread. Null until
   *  the first user message of a fresh thread is sent — at which point we
   *  POST /api/ai/conversations/ to mint one. */
  conversationId: string | null;
  /** Persisted model selection — sent on every /propose call. Sonnet by
   *  default; the drawer header has a dropdown for the user to switch. */
  model: AgentModelId;
  /** Per-call cap on tool-use rounds (1..backend ceiling). Lower = cheaper
   *  but the agent has fewer chances to gather context for tricky prompts. */
  maxRounds: number;
  /** When true, the proposal confirm card promotes the Dry-run button to
   *  primary. Belt-and-suspenders for users who want to always preview
   *  before pushing — useful while building trust in the agent. */
  dryRunDefault: boolean;

  setOpen: (v: boolean) => void;
  toggle: () => void;
  setModel: (m: AgentModelId) => void;
  setMaxRounds: (n: number) => void;
  setDryRunDefault: (v: boolean) => void;
  /** Send a prompt → /propose → append to transcript. */
  send: (prompt: string) => Promise<void>;
  /** Abort the in-flight /propose call. UI shows the existing user
   *  message + an error bubble explaining the cancel. */
  abort: () => void;
  /** Confirm + execute a pending proposal at index `i` in the transcript. */
  execute: (i: number, opts?: { dryRun?: boolean }) => Promise<void>;
  /** Cancel a pending proposal at index `i` (UI-only — no server call). */
  cancel: (i: number) => void;
  /** Wipe the open transcript without closing the drawer. Server-side
   *  conversation row is left intact (the /agent page can still load it). */
  clear: () => void;
  /** Start a fresh conversation. Like clear() but also forgets the
   *  conversationId so the next send mints a new server row. */
  newConversation: () => void;

  /* ---------- server-side history ---------- */
  /** Fetch the user's archived conversations. Returned newest-activity-first. */
  listConversations: () => Promise<ServerConversation[]>;
  /** Replace the open transcript with the persisted version of `id`. */
  loadConversation: (id: string) => Promise<void>;
  /** Delete a conversation server-side. If `id` is the current one,
   *  also wipes the local transcript. */
  deleteConversation: (id: string) => Promise<void>;
}

// Held outside the store because AbortControllers aren't JSON-serializable
// and we don't want them in persisted state. Reset whenever a /propose
// kicks off; signalled by abort().
let _inFlightController: AbortController | null = null;

const isoNow = () => new Date().toISOString();

/** Best-effort POST to /api/ai/conversations/ — returns the new id or null
 *  if the call failed (caller proceeds with conversationId=null and we'll
 *  retry on the next send). */
async function _createServerConversation(title: string, model: string): Promise<string | null> {
  try {
    const r = await apiPost<{ id: string }>("/ai/conversations/", { title, model });
    return r.id;
  } catch {
    return null;
  }
}

/** Best-effort POST .../messages/ — returns the new server message id or
 *  null on failure. Local state persists either way. */
async function _appendServerMessage(
  convId: string,
  kind: AgentMessage["kind"],
  payload: Record<string, unknown>,
): Promise<string | null> {
  try {
    const r = await apiPost<{ id: string; seq: number }>(
      `/ai/conversations/${encodeURIComponent(convId)}/messages/`,
      { kind, payload },
    );
    return r.id;
  } catch {
    return null;
  }
}

/** Best-effort PATCH .../messages/{id} — used when a proposal status flips. */
async function _patchServerMessage(
  convId: string,
  msgId: string,
  payload: Record<string, unknown>,
): Promise<void> {
  try {
    await apiPatch(
      `/ai/conversations/${encodeURIComponent(convId)}/messages/${encodeURIComponent(msgId)}`,
      { payload },
    );
  } catch {
    // Mirror failure isn't fatal — the local state is still right.
  }
}

/** Strip a frontend AgentMessage down to the JSON-safe payload the server
 *  stores (everything except `kind` and `serverId`). */
function _toServerPayload(m: AgentMessage): Record<string, unknown> {
  // Spread, then nuke the fields the server doesn't track. Cheaper than
  // typing one builder per kind, and the server is shape-agnostic anyway.
  const out = { ...m } as Record<string, unknown>;
  delete out.kind;
  delete out.serverId;
  return out;
}

export const useAiAgent = create<State>()(
  persist(
    (set, get) => ({
  open: false,
  status: "idle",
  messages: [],
  conversationId: null,
  model: "claude-sonnet-4-6",
  maxRounds: 10,
  dryRunDefault: false,

  setOpen:           (v) => set({ open: v }),
  toggle:            () => set((s) => ({ open: !s.open })),
  setModel:          (m) => set({ model: m }),
  // Clamp at the store level too — backend re-clamps but defending here
  // means a slider with a wonky min/max can't poison the persisted value.
  setMaxRounds:      (n) => set({ maxRounds: Math.max(1, Math.min(20, Math.floor(n))) }),
  setDryRunDefault:  (v) => set({ dryRunDefault: !!v }),

  send: async (prompt) => {
    const trimmed = prompt.trim();
    if (!trimmed || get().status === "thinking") return;
    // Build history from current transcript BEFORE we append the new user
    // message — text-only, capped at the last 12 turns so a long session
    // doesn't blow our token budget. Proposals are summarized as a short
    // string so the model knows it proposed an action even if the user
    // didn't run it yet.
    const HISTORY_TURN_LIMIT = 12;
    const recent = get().messages.slice(-HISTORY_TURN_LIMIT);
    const history = recent
      .map((m): { role: "user" | "assistant"; content: string } | null => {
        if (m.kind === "user")    return { role: "user",      content: m.text };
        if (m.kind === "answer")  return { role: "assistant", content: m.text };
        if (m.kind === "proposal") {
          const verb = m.status === "executed" ? "ran" : m.status === "cancelled" ? "cancelled" : "proposed";
          return {
            role: "assistant",
            content: `${verb} ${m.proposal.tool} with args ${JSON.stringify(m.proposal.args)}`,
          };
        }
        return null; // errors don't go into history
      })
      .filter((x): x is { role: "user" | "assistant"; content: string } => x !== null);

    // Lazily mint a server-side conversation on the first send of a thread.
    // Title is the first ~80 chars of the prompt so /agent's list reads as
    // a meaningful preview. If the create call fails (network/auth/etc.)
    // we proceed local-only — the user's chat still works.
    let convId = get().conversationId;
    if (!convId) {
      const title = trimmed.length > 80 ? trimmed.slice(0, 77) + "…" : trimmed;
      convId = await _createServerConversation(title, get().model);
      if (convId) set({ conversationId: convId });
    }

    // Build the user message (frontend shape) and persist locally first so
    // the bubble appears immediately. Mirror to server in the background;
    // when it returns, splice the serverId in so PATCHes can find this row.
    const userMsg: AgentMessage = { kind: "user", text: trimmed, sentAt: isoNow() };
    set((s) => ({
      status: "thinking",
      messages: [...s.messages, userMsg],
    }));
    const userIdx = get().messages.length - 1;
    if (convId) {
      void _appendServerMessage(convId, "user", _toServerPayload(userMsg)).then((sid) => {
        if (!sid) return;
        set((s) => ({
          messages: s.messages.map((x, idx) =>
            idx === userIdx && x.kind === "user" ? { ...x, serverId: sid } : x,
          ),
        }));
      });
    }
    // Set up abort plumbing — abort() flips this and the fetch unwinds via
    // AbortError. Reset on every send so a stale controller from a prior
    // turn can't accidentally cancel this one.
    _inFlightController = new AbortController();
    try {
      const raw = await apiPost<unknown>("/ai/propose",
        { prompt: trimmed, history, model: get().model, max_rounds: get().maxRounds },
        _inFlightController.signal);
      // Trust-but-verify: the shim's response shape is locked, but we still
      // gate by `type` so a future shape change can't crash the UI.
      const r = raw as {
        type: "answer" | "proposal" | "error";
        text?: string;
        proposal?: AgentProposal;
        error?: string;
        readTrace?: ReadTraceEntry[];
        model?: string;
        elapsedMs?: number;
      };
      const base = {
        readTrace: r.readTrace ?? [],
        model:     r.model ?? "?",
        elapsedMs: r.elapsedMs ?? 0,
        receivedAt: isoNow(),
      };
      const next: AgentMessage =
        r.type === "answer"
          ? { kind: "answer", text: r.text ?? "(no reply)", ...base }
          : r.type === "proposal" && r.proposal
            ? { kind: "proposal", proposal: r.proposal, status: "pending", ...base }
            : { kind: "error", text: r.error ?? "agent returned an unexpected response", receivedAt: isoNow() };
      set((s) => ({ status: "idle", messages: [...s.messages, next] }));
      // Mirror the reply too. Same fire-and-forget pattern.
      const replyIdx = get().messages.length - 1;
      if (convId) {
        void _appendServerMessage(convId, next.kind, _toServerPayload(next)).then((sid) => {
          if (!sid) return;
          set((s) => ({
            messages: s.messages.map((x, idx) =>
              idx === replyIdx && x.kind === next.kind ? ({ ...x, serverId: sid } as AgentMessage) : x,
            ),
          }));
        });
      }
    } catch (err) {
      // AbortError from a user-clicked Cancel button — show a softer hint
      // than a generic API error. The native fetch DOMException has
      // name === "AbortError" when AbortController.abort() fires.
      const isAbort = err instanceof DOMException && err.name === "AbortError";
      const msg = isAbort
        ? "Cancelled"
        : err instanceof ApiError
          ? err.status === 503
            ? "AI agent is temporarily unavailable"
            : `API error ${err.status}`
          : err instanceof Error
            ? err.message
            : "unknown error";
      const errMsg: AgentMessage = { kind: "error", text: msg, receivedAt: isoNow() };
      set((s) => ({
        status: "idle",
        messages: [...s.messages, errMsg],
      }));
      const errIdx = get().messages.length - 1;
      if (convId) {
        void _appendServerMessage(convId, "error", _toServerPayload(errMsg)).then((sid) => {
          if (!sid) return;
          set((s) => ({
            messages: s.messages.map((x, idx) =>
              idx === errIdx && x.kind === "error" ? { ...x, serverId: sid } : x,
            ),
          }));
        });
      }
    } finally {
      _inFlightController = null;
    }
  },

  abort: () => {
    if (_inFlightController) {
      _inFlightController.abort();
      _inFlightController = null;
    }
  },

  execute: async (i, opts) => {
    const m = get().messages[i];
    if (!m || m.kind !== "proposal" || m.status !== "pending") return;
    const convId = get().conversationId;
    // Mark executing so the UI disables the buttons. `as const` keeps the
    // literal-union type intact through the spread (otherwise `status`
    // widens to `string` and TypeScript rejects the assignment).
    set((s) => ({
      messages: s.messages.map((x, idx) =>
        idx === i && x.kind === "proposal"
          ? { ...x, status: "executing" as const }
          : x,
      ),
    }));
    try {
      const raw = await apiPost<unknown>("/ai/execute-proposal", {
        proposal: m.proposal,
        dry_run: !!opts?.dryRun,
      });
      const r = raw as { id?: string };
      set((s) => ({
        messages: s.messages.map((x, idx) =>
          idx === i && x.kind === "proposal"
            ? { ...x, status: "executed" as const, runId: r.id }
            : x,
        ),
      }));
      // Mirror the new payload to the server so a future page-load reflects
      // the executed state. Best-effort.
      const updated = get().messages[i];
      if (convId && updated && updated.kind === "proposal" && updated.serverId) {
        void _patchServerMessage(convId, updated.serverId, _toServerPayload(updated));
      }
    } catch (err) {
      // Roll back to "pending" so the user can retry. Surface error inline.
      const text =
        err instanceof ApiError
          ? `Execute failed: API error ${err.status}`
          : err instanceof Error
            ? `Execute failed: ${err.message}`
            : "Execute failed";
      set((s) => ({
        messages: [
          ...s.messages.map((x, idx) =>
            idx === i && x.kind === "proposal"
              ? { ...x, status: "pending" as const }
              : x,
          ),
          { kind: "error" as const, text, receivedAt: isoNow() },
        ],
      }));
      const errIdx = get().messages.length - 1;
      if (convId) {
        const errMsg = get().messages[errIdx];
        if (errMsg && errMsg.kind === "error") {
          void _appendServerMessage(convId, "error", _toServerPayload(errMsg)).then((sid) => {
            if (!sid) return;
            set((s) => ({
              messages: s.messages.map((x, idx) =>
                idx === errIdx && x.kind === "error" ? { ...x, serverId: sid } : x,
              ),
            }));
          });
        }
        // Also patch the proposal (now back to pending) so the server reflects truth.
        const proposal = get().messages[i];
        if (proposal && proposal.kind === "proposal" && proposal.serverId) {
          void _patchServerMessage(convId, proposal.serverId, _toServerPayload(proposal));
        }
      }
    }
  },

  cancel: (i) => {
    set((s) => ({
      messages: s.messages.map((x, idx) =>
        idx === i && x.kind === "proposal" && x.status === "pending"
          ? { ...x, status: "cancelled" as const }
          : x,
      ),
    }));
    // Mirror status flip to the server — best-effort.
    const m = get().messages[i];
    const convId = get().conversationId;
    if (convId && m && m.kind === "proposal" && m.serverId) {
      void _patchServerMessage(convId, m.serverId, _toServerPayload(m));
    }
  },

  clear: () => set({ messages: [] }),

  newConversation: () => set({ messages: [], conversationId: null }),

  /* ---------- server-side history ---------- */

  listConversations: async () => {
    try {
      return await apiGet<ServerConversation[]>("/ai/conversations/");
    } catch {
      return [];
    }
  },

  loadConversation: async (id) => {
    try {
      const conv = await apiGet<{
        id: string;
        title: string;
        model: string | null;
        startedAt: string;
        lastAt: string;
        messages: { id: string; seq: number; kind: AgentMessage["kind"]; payload: Record<string, unknown>; createdAt: string }[];
      }>(`/ai/conversations/${encodeURIComponent(id)}`);
      // Reconstruct the AgentMessage shape: spread payload, restore kind,
      // attach serverId so subsequent PATCHes hit the right row.
      // Reset any "executing" status at load — that flag only makes sense
      // while a /execute-proposal call is in flight. Same defensive demote
      // as onRehydrateStorage applies to localStorage.
      const messages: AgentMessage[] = conv.messages.map((m) => {
        const restored = { kind: m.kind, ...(m.payload as object), serverId: m.id } as AgentMessage;
        if (restored.kind === "proposal" && restored.status === "executing") {
          return { ...restored, status: "pending" as const };
        }
        return restored;
      });
      set({ conversationId: conv.id, messages, open: true });
    } catch {
      // Silent failure — caller can surface from listConversations() retry.
    }
  },

  deleteConversation: async (id) => {
    try {
      await apiDel(`/ai/conversations/${encodeURIComponent(id)}`);
    } catch {
      // Non-fatal — the next list call will reflect server truth.
    }
    if (get().conversationId === id) {
      set({ messages: [], conversationId: null });
    }
  },
    }),
    {
      name: "opus.ai-agent",
      // Persist only the user-visible bits. The transient `status` and
      // `open` flags shouldn't survive a refresh — boot fresh so a stuck
      // "thinking…" from a crashed tab doesn't get reanimated.
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({
        messages: s.messages,
        conversationId: s.conversationId,
        model: s.model,
        maxRounds: s.maxRounds,
        dryRunDefault: s.dryRunDefault,
      }) as Pick<State, "messages" | "conversationId" | "model" | "maxRounds" | "dryRunDefault">,
      // Cap to last 200 messages so a long power-user session doesn't
      // blow out the localStorage quota (typically 5MB).
      version: 2,
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        // Cap the persisted history so a long-running session doesn't blow
        // out the localStorage quota (typically 5MB).
        if (state.messages.length > 200) {
          state.messages = state.messages.slice(-200);
        }
        // Reset any proposal stuck in "executing" — that status only lives
        // in-memory while a /execute-proposal call is in flight. If the tab
        // crashed or was closed mid-call, the persisted state would lock the
        // card forever (no buttons to click). Demoting back to "pending"
        // lets the user retry or cancel.
        state.messages = state.messages.map((m) =>
          m.kind === "proposal" && m.status === "executing"
            ? { ...m, status: "pending" as const }
            : m,
        );
      },
    },
  ),
);
